import logEvent from 'analytics/logEvent';
import cn from 'classnames';
import { QueryParser } from 'domains/common/queryParser';
import useTranslationAndTypograph from 'hooks/useTranslationAndTypograph';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import InputText from 'components/basic/InputText';
import { CircularProgress } from 'components/basic/Loaders';
import { BodyL, Caption } from 'components/basic/Typography';
import useSplitYearlyCost from 'components/complex/payments/Product/useSplitYearlyCost';

import { useAppDispatch, useAppSelector } from 'redux-store';
import couponCheckThunk from 'redux-store/thunks/products/couponCheckThunk';

import s from './PromocodeForm.module.scss';

interface Props {
  selectedPlanTariff?: string;
  couponIsValid?: null | boolean;
  className?: string;
}

export default function PromocodeForm({
  selectedPlanTariff,
  couponIsValid,
  className,
}: Props): JSX.Element {
  const [isPromocodeFormShowing, setPromocodeFormShowing] = useState(false);
  const t = useTranslationAndTypograph();
  const router = useRouter();
  const [promocode, setPromocode] = useState(router.query.coupon as string);
  const dispatch = useAppDispatch();
  const productsStore = useAppSelector(state => state.products);
  const [isPromocodeActivated, setPromocodeActivated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [promocodeError, setPromocodeError] = useState<
    null | JSX.Element | string
  >(null);
  const [currentDiscount, setCurrentDiscount] = useState(0);
  const { isNeedToSplitYearPrice } = useSplitYearlyCost();

  useEffect(() => {
    if (couponIsValid === false) {
      setPromocodeError(
        t(
          'Couldn’t apply your code. Make sure you entered it correctly & remember that used codes can’t be applied',
        ),
      );
      setPromocodeFormShowing(true);

      return;
    }

    setPromocodeError(null);
  }, [couponIsValid]);

  async function applyPromocode(): Promise<void> {
    logEvent('Web_Payment_Coupon_Apply_Button_Clicked', {
      tariff: selectedPlanTariff,
      coupon: promocode,
    });
    setPromocodeError(null);

    const productsIds = productsStore.products
      .map(product => product.id)
      .toString();

    setIsLoading(true);

    if (false) {
      //костыль, потому что в этом случае встарый пересчёт цены, который происходит при SSR
      window.location.reload();
    } 
    else {
      try {
        const res = await dispatch(
          couponCheckThunk({
            coupon_code: promocode,
            product_ids: productsIds,
          }),
        ).unwrap();

        if (res.success === false && res.error === 'product_id not found') {
          setPromocodeError(
            t(
              'Couldn’t apply your code. Make sure you entered it correctly & remember that used codes can’t be applied',
            ),
          );
        } else {
          const search = new QueryParser(window.location.search).set({
            coupon: promocode,
          });
          router.push(`${location.pathname}${search.toString()}`);
        }
      } finally {
        setIsLoading(false);
      }
    }
  }

  useEffect(() => {
    if (productsStore.isFetched && productsStore.products[0]) {
      const [product] = productsStore.products;
      const discount = Number(product.prices[0].discount);

      if (discount) {
        setPromocodeActivated(true);
        setCurrentDiscount(discount);
      }
    }
  }, [productsStore.isFetched, productsStore.products]);

  if (!productsStore.isFetched || isLoading) {
    return (
      <div className={s.loaderWrapper}>
        <CircularProgress />
      </div>
    );
  }

  function toggleForm(): void {
    logEvent('Web_Payment_Coupon_Expand_Button_Clicked', {
      tariff: selectedPlanTariff,
      coupon: promocode,
    });
    setPromocodeFormShowing(isShowing => !isShowing);
  }

  return (
    <div className={cn(s.wrapper, className)}>
      {currentDiscount > 0 && (
        <div className={s.description}>
          {t(`You now get currentDiscount off`, {
            currentDiscount,
          })}
        </div>
      )}

      {!isPromocodeFormShowing && (
        <div
          className={cn(s.formTrigger, 'cy-enter-promocode-button')}
          onClick={toggleForm}
        >
          {t(
            isPromocodeActivated ? 'Enter another code' : 'Enter a promo code',
          )}
        </div>
      )}

      {isPromocodeFormShowing && (
        <>
          <div className={s.formWrapper}>
            <InputText
              value={promocode}
              name="promoCode"
              labelText={t('Your promo code')}
              onChange={setPromocode}
              className="cy-promocode-input"
            />

            <BodyL
              className={cn(s.apply, 'cy-apply-promocode', {
                [s.disabled]: !promocode,
              })}
              onClick={applyPromocode}
            >
              {t('apply')}
            </BodyL>
          </div>
          <Caption className={s.error}>{promocodeError}</Caption>
        </>
      )}
    </div>
  );
}
